/**
 * Customer-facing release notes: the types and the pure logic that both backends share.
 * This folder holds plain data and pure functions. It imports no Node module and no react-intl.
 */

export const releaseLocales = ['en', 'de', 'es', 'fr', 'it', 'nl', 'pl', 'pt', 'sv'] as const;

export type ReleaseLocale = (typeof releaseLocales)[number];

/** English is required because it is the fallback. The other locales are optional. */
export type LocalizedText = { en: string } & Partial<Record<ReleaseLocale, string>>;

export type ReleaseCategory = 'feature' | 'improvement' | 'fix';

/**
 * Where a customer reads the note. Each store build names only its own platform.
 * `web` is the browser app and the website.
 */
export const releaseSurfaces = ['web', 'ios', 'android'] as const;

export type ReleaseSurface = (typeof releaseSurfaces)[number];

/** The accent colours of a story card. */
export const storyAccents = ['yellow', 'pink', 'purple', 'green', 'blue'] as const;

export type StoryAccent = (typeof storyAccents)[number];

/**
 * A promo image of a story, hosted by `pnpm release:host-images`: WebP, at most 1600 px wide,
 * named by its content. The size lets a card reserve the space before the image loads.
 */
export interface ReleaseImage {
  url: string;
  width: number;
  height: number;
}

/**
 * The rich wording of an entry. Every `highlight` entry has one. A text field may mark bold
 * with `**like this**` and nothing else. `parseStoryMarkup` turns it into runs.
 */
export interface ReleaseStory {
  /** The pill above the headline: a few words that name the area. */
  tag: LocalizedText;
  /** One short sentence. */
  headline: LocalizedText;
  /** Two to four sentences. */
  lede: LocalizedText;
  /** The Before / Now pair. Set both or neither. */
  before?: LocalizedText;
  now?: LocalizedText;
  /** Small print under the card. */
  note?: LocalizedText;
  accent: StoryAccent;
  /** The promo image above the tag. Every surface shows the same image. */
  image?: ReleaseImage;
}

export interface ReleaseEntryWording {
  title: LocalizedText;
  body: LocalizedText;
  /** Story fields to replace on this platform. The accent and the image never change. */
  story?: Partial<Omit<ReleaseStory, 'accent' | 'image'>>;
}

export interface ReleaseEntry {
  /** Stable, kebab-case and unique inside its release. */
  id: string;
  category: ReleaseCategory;
  /** The author's suggestion that this is a headline change. */
  highlight?: boolean;
  /** The in-app surfaces that show this entry. Absent means all of them. */
  surfaces?: ReleaseSurface[];
  /** One line. Web wording: it never names iOS or Android. */
  title: LocalizedText;
  /** One or two sentences. Web wording. */
  body: LocalizedText;
  /** iOS wording where the web wording is wrong on an iPhone. It never names Android. */
  ios?: Partial<ReleaseEntryWording>;
  /** Android wording where the web wording is wrong on Android. It never names Apple. */
  android?: Partial<ReleaseEntryWording>;
  /** The card wording. Required on a `highlight` entry. */
  story?: ReleaseStory;
}

export interface Release {
  /** The app version, for example `11.1.0`. */
  version: string;
  /** ISO date, YYYY-MM-DD. */
  releasedAt: string;
  /** The one-line pitch. `**bold**` is allowed. Web wording. */
  summary?: LocalizedText;
  entries: ReleaseEntry[];
}

/** One run of story text. `parseStoryMarkup` returns these. */
export interface StoryRun {
  text: string;
  bold: boolean;
}

export interface ResolvedReleaseStory {
  tag: string;
  headline: string;
  lede: string;
  before?: string;
  now?: string;
  note?: string;
  accent: StoryAccent;
  /** The alt text is the headline. */
  image?: ReleaseImage;
}

/** One entry resolved for one surface and one locale. */
export interface ResolvedReleaseEntry {
  /** `${sourceVersion}-${entryId}` */
  id: string;
  sourceVersion: string;
  category: ReleaseCategory;
  highlight: boolean;
  title: string;
  body: string;
  story?: ResolvedReleaseStory;
}

// API contract: both backends return these shapes.
export interface PublishedBrief {
  version: string;
  releasedAt: string;
  summary: string | null;
  entryCount: number;
  entries: ResolvedReleaseEntry[];
}

export interface ChangelogRelease {
  version: string;
  releasedAt: string;
  publishedAt: string;
  summary: string | null;
  entries: ResolvedReleaseEntry[];
}
